import type { SimpleDate } from "@wkt/api/employee-calendar/employeeCalendarDTO";

export interface ProcedureDocumentDTO {
  id?: number;
  documentName?: string | null;
}

export interface PaidAbsenceCategoryDTO {
  id: number;
  name?: string | null;
  isEnabled?: boolean | null;
}

export interface HoursBagDTO {
  id?: number | null;
  companyId?: number | null;
  days?: number | null;
  label?: string | null;
  allowNegative?: boolean | null;
  enabled?: boolean | null;
}

export interface VacationEntryDTO {
  id?: number | null;
  companyId?: number | null;
  employeeId?: number | null;
  exercise?: number | null;
  hoursBagType?: number | null;
  hoursBagDelta?: number | null;
  hoursBagTotal?: number | null;
  sortDate?: string | null;
  paidAbsenceCategory?: number | null;
  fromDate?: SimpleDate | null;
  recordDate?: string | null;
  recordInfo?: string | null;
  recordStatus?: number | null;
  recordTitle?: string | null;
  recordType?: number | null;
  toDate?: SimpleDate | null;
  externalId?: string | null;
  sourceProcedureDefinitionId?: number | null;
  expirationDate?: SimpleDate | null;
  expiredVacationId?: number | null;
  remainingDays?: number | null;
}

export interface VacationDataDTO {
  vacationData?: VacationEntryDTO[] | null;
}

export interface ProcedureTimeDTO {
  hours: number;
  minutes: number;
  seconds?: number | null;
}

export interface FullProcedureDefinitionDTO {
  id: number;
  companyId?: number | null;
  accessType?: number | null;
  workplaces?: string | null;
  departments?: string | null;
  legalEntities?: string | null;
  projects?: string | null;
  employees?: string | null;
  approverType?: number | null;
  notifiedType?: number | null;
  name?: string | null;
  description?: string | null;
  enabled?: boolean;
  documentCategoryId?: number | null;
  iconType?: string | null;
  procedureClass?: string | null;
  showFromDate?: boolean;
  showToDate?: boolean;
  showFromTime?: boolean;
  showToTime?: boolean;
  showQuantity?: boolean;
  showFreeText?: boolean;
  showVacationDays?: boolean;
  showHoursReference?: boolean;
  hoursReference?: number | null;
  showProject?: boolean;
  showActivityType?: boolean;
  allowUploadDocument?: boolean;
  enableAbsenceCategory?: boolean;
  readonlyDefinition?: boolean;
  processOnApproval?: boolean;
  hoursBagTypes?: string | null;
  paidAbsencesCategories?: string | null;
  freeTextLabel?: string | null;
  quantityLabel?: string | null;
  fromDateLabel?: string | null;
  toDateLabel?: string | null;
  fromTimeLabel?: string | null;
  toTimeLabel?: string | null;
  activityTypeLabel?: string | null;
  projectLabel?: string | null;
  requestSubtype?: number | null;
  order?: number | null;
  notifiedEmployees?: string | null;
  approverEmployees?: string | null;
  activityTypeId?: number | null;
  defaultQuantity?: number | null;
  externalId?: string | null;
}

export interface FullProcedureRequestDTO {
  id: number;
  companyId?: number | null;
  workplaceId?: number | null;
  departmentId?: number | null;
  supervisorId?: number | null;
  employeeId?: number | null;
  adminId?: number | null;
  username?: string | null;
  firstname?: string | null;
  lastname?: string | null;
  profilePicture?: string | null;
  requestDate?: string | null;
  updateDate?: string | null;
  requestInfo?: string | null;
  requestStatus?: number | null;
  requestTitle?: string | null;
  requestType?: number | null;
  fromDate?: SimpleDate | null;
  fromHour?: ProcedureTimeDTO | null;
  toDate?: SimpleDate | null;
  toHour?: ProcedureTimeDTO | null;
  approverComment?: string | null;
  adminFirstname?: string | null;
  adminLastname?: string | null;
  quantity?: number | null;
  notifiedEmployees?: string | null;
  approverEmployees?: string | null;
  projectId?: number | null;
  projectShortName?: string | null;
  projectDescription?: string | null;
  activityTypeId?: number | null;
  activityTypeDescription?: string | null;
  activityTypeInternalCode?: string | null;
  hoursBagType?: number | null;
  hoursBagDelta?: number;
  extraDeltaOld?: number;
  vacationDeltaOld?: number;
  manualCalculationVacation?: boolean;
  expenseSheetId?: number | null;
  requestReference?: string | null;
  documentIds?: string | null;
  documents?: ProcedureDocumentDTO[] | null;
  paidAbsenceCategoryId?: number | null;
  paidAbsenceCategoryName?: string | null;
}

export interface FullEmployeeDTO {
  id: number;
  firstname?: string | null;
  lastname?: string | null;
  profilePicture?: string | null;
  currentVacationDays?: number | null;
  currentExtraDays?: number | null;
  currentHoursBag1Days?: number | null;
  currentHoursBag2Days?: number | null;
  currentHoursBag3Days?: number | null;
  currentHoursBag4Days?: number | null;
  currentHoursBag5Days?: number | null;
}

export interface ProcedureDataDTO {
  procedureRequests?: FullProcedureRequestDTO[];
  approverRequests?: FullProcedureRequestDTO[];
  approverEmployees?: FullEmployeeDTO[];
}

export interface GenericServiceResponseDTO {
  rsOK?: boolean;
  message?: string | null;
  resultCode?: number;
}

export interface ProcedureServiceResponseDTO {
  rsOK?: boolean;
  message?: string | null;
  procedure?: FullProcedureRequestDTO | null;
}

export interface ProcedureDocumentUploadResponseDTO {
  requestId: number;
  documentId: number;
}

export const ProcedureRequestStatus = {
  PREPARING: -1,
  PENDING: 0,
  APPROVED: 1,
  DECLINED: 2,
  CANCELED: 3,
  PROCESSED: 4,
} as const;

export const ProcedureDefinitionClass = {
  VACATION_LEAVE: "VACATION_LEAVE",
} as const;
